/**
 * @file PatioCard.js
 * @description Componente de visualización detallada con COLUMNAS DINÁMICAS (Optimizado)
 */
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { ESTATUS } from '../../config/config';
import {
  ChevronLeft, ChevronUp, ChevronDown, ArrowUpDown,
  MapPin, Building, Package, Search, FileDown, FileText,
  Settings // Icono de configuración
} from 'lucide-react';

// Herramientas de columnas dinámicas
import { useTableColumns } from '../../hooks/useTableColumns';
import ColumnReorderPopup from '../popups/ColumnReorderPopup';
import { formatDate, formatDateTime } from '../../utils/vehicleHelpers';
import { exportSitioConSubsitiosEnUnaHoja } from '../../utils/exporters/vehiculosExporter';
import CarTraceabilityPopUp from '../popups/CarTraceabilityPopUp';

// Importamos TODO desde tu patiosData centralizado
import {
  marcasColors,
  marcasLogos,
  getSitioCategoryColor,
  getSubSitioType,
  getSubSitioColor,
  vehiculoEstaEnPatio,
  getStatusName,
  getStatusColor
} from '../../data/patiosData';


// 1. DEFINICIÓN DEL UNIVERSO DE COLUMNAS
const ALL_COLUMNS_CONFIG = [
  { id: 'VIN', label: 'VIN', sortable: true, width: 'min-w-[160px]' },
  { id: 'NoInventario', label: 'No. Inventario', sortable: true, width: 'min-w-[120px]' },
  { id: 'Marca', label: 'Marca', sortable: true, width: 'min-w-[100px]' },
  { id: 'Familia', label: 'Familia', sortable: true, width: 'min-w-[120px]' },
  { id: 'Modelo', label: 'Modelo', sortable: true, width: 'min-w-[120px]' },
  { id: 'Version', label: 'Versión', sortable: true, width: 'min-w-[150px]' },
  { id: 'AnoModelo', label: 'Año', sortable: true, width: 'min-w-[80px]' }, 
  { id: 'Catalogo', label: 'Catálogo', sortable: true, width: 'min-w-[120px]' },
  { id: 'ColorExterior', label: 'Color Ext.', sortable: true, width: 'min-w-[120px]' },
  { id: 'ColorInterior', label: 'Color Int.', sortable: true, width: 'min-w-[120px]' },
  { id: 'Ubicacion', label: 'Ubicación PAI', sortable: true, width: 'min-w-[150px]' },
  { id: 'estatus', label: 'Estatus', sortable: true, width: 'min-w-[200px]' },
  { id: 'detectado_rfid', label: 'RFID', sortable: true, width: 'min-w-[80px]' },
  { id: 'last_read', label: 'Última Lectura', sortable: true, width: 'min-w-[140px]' },
  { id: 'VTE_DOCTO', label: 'N° Factura', sortable: true, width: 'min-w-[140px]' },
  { id: 'vendedor', label: 'Vendedor', sortable: true, width: 'min-w-[200px]' },
  { id: 'FechaRecibo', label: 'F. Recibido', sortable: true, width: 'min-w-[120px]' }
];

// 2. COLUMNAS POR DEFECTO
const DEFAULT_COLUMN_IDS = [
  'VIN', 'NoInventario', 'Marca', 'Familia', 'AnoModelo',
  'Catalogo', 'ColorExterior', 'ColorInterior', 'VTE_DOCTO', 'vendedor', 'estatus',
  'detectado_rfid', 'last_read', 'FechaRecibo'
];

const PatioCard = ({ sitio, subSitio, vehiculos = [], onBackClick }) => {
  // ===== ESTADO LOCAL =====
  const [sortConfig, setSortConfig] = useState({ key: 'last_read', direction: 'desc' });
  const [searchVIN, setSearchVIN] = useState('');
  const [isColumnPopupOpen, setIsColumnPopupOpen] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);

  // ===== HOOK DE COLUMNAS DINÁMICAS =====
  const {
    visibleColumns,
    saveColumnOrder,
    isLoadingConfig
  } = useTableColumns('patio_main_table', ALL_COLUMNS_CONFIG, DEFAULT_COLUMN_IDS);

  // ===== FILTRADO =====
  const vehiculosEnPatio = useMemo(() => {
    return vehiculos.filter(vehiculo => vehiculo.lectura_rfid != null);
  }, [vehiculos]);

  const vehiculosFiltrados = useMemo(() => {
    if (!searchVIN.trim()) return vehiculosEnPatio;
    const searchTerm = searchVIN.toLowerCase().trim();
    return vehiculosEnPatio.filter(vehiculo =>
      vehiculo.VIN?.toLowerCase().includes(searchTerm) ||
      vehiculo.NoInventario?.toLowerCase().includes(searchTerm)
    );
  }, [vehiculosEnPatio, searchVIN]);

  // ===== EXPORT =====
  const handleExportExcel = async () => {
    const visibleKeys = visibleColumns.map(c => c.id);
    const rows = getSortedVehicles(vehiculosFiltrados);
    const secciones = [{
      nombre: isSubSitio ? displayInfo.nombre : 'Inventario',
      vehiculos: rows,
    }];
    const meta = {
      estado: displayInfo.estado,
      sitio: isSubSitio ? (displayInfo.sitioNombre || displayInfo.nombre) : displayInfo.nombre,
    };
    await exportSitioConSubsitiosEnUnaHoja(secciones, visibleKeys, meta);
  };

  const handleExportPDF = async () => {
    const visibleKeys = visibleColumns.map(c => c.id);
    const rows = getSortedVehicles(vehiculosFiltrados);
    const meta = isSubSitio
      ? { sitio: displayInfo.sitioNombre, subSitio: displayInfo.nombre }
      : { sitio: displayInfo.nombre };
    const { exportVehiculosPDF } = await import('../../utils/exporters/vehiculosPdfExporter');
    exportVehiculosPDF(rows, visibleKeys, meta);
  };

  useEffect(() => {
    // Log opcional para debug
  }, [vehiculos]);

  const currentItem = subSitio || sitio;
  const isSubSitio = !!subSitio;

  // ===== HELPERS DE FORMATO =====
  const getColorCode = (colorName) => {
    if (!colorName) return '#9CA3AF';
    const colorMap = { 'NEGRO': '#000000', 'BLANCO': '#FFFFFF', 'ROJO': '#EF4444', 'AZUL': '#3B82F6', 'PLATA': '#C0C0C0', 'GRIS': '#6B7280', 'VERDE': '#10B981' };
    const key = Object.keys(colorMap).find(k => colorName.toUpperCase().includes(k));
    return colorMap[key] || '#9CA3AF';
  };

  const getSortedVehicles = (vehicles) => {
    if (!sortConfig.key) return vehicles;
    return [...vehicles].sort((a, b) => {
      let aVal = a[sortConfig.key], bVal = b[sortConfig.key];

      if (sortConfig.key === 'last_read') {
        aVal = a.lectura_rfid?.last_read;
        bVal = b.lectura_rfid?.last_read;
      }

      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;

      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const handleSort = (key) => {
    setSortConfig(current => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) return <ArrowUpDown className="w-3 h-3 text-gray-400" />;
    return sortConfig.direction === 'asc' ? <ChevronUp className="w-3 h-3 text-blue-600" /> : <ChevronDown className="w-3 h-3 text-blue-600" />;
  };

  let displayInfo = {};
  if (currentItem) {
    if (isSubSitio) {
      const tipo = getSubSitioType(currentItem.nombre);
      displayInfo = {
        nombre: currentItem.nombre,
        tipo: tipo.charAt(0).toUpperCase() + tipo.slice(1),
        colorClass: getSubSitioColor(tipo),
        sitioNombre: currentItem.sitio,
        estado: currentItem.estado,
        icon: Package
      };
    } else {
      displayInfo = {
        nombre: currentItem.nombre,
        tipo: currentItem.categoria,
        colorClass: getSitioCategoryColor(currentItem.categoria),
        sitioNombre: null,
        estado: currentItem.estado,
        icon: Building
      };
    }
  }

  const shouldCenterColumn = (columnId) => {
    const centeredColumns = ['AnoModelo', 'Catalogo', 'FechaRecibo'];
    return centeredColumns.includes(columnId);
  };

  // 3. RENDERIZADOR DINÁMICO
  const renderCellContent = useCallback((vehicle, columnId) => {
    switch (columnId) {
      case 'VIN':
        return <span className="font-mono text-xs whitespace-nowrap">{vehicle.VIN}</span>;

      case 'NoInventario':
        return <span className="font-mono text-xs whitespace-nowrap">{vehicle.NoInventario || '-'}</span>;

      case 'Marca':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
            style={{
              backgroundColor: `${marcasColors[vehicle.Marca]}20` || '#E5E7EB',
              color: marcasColors[vehicle.Marca] || '#6B7280'
            }}>
            {vehicle.Marca}
          </span>
        );

      case 'ColorExterior':
      case 'ColorInterior':
        const colorName = vehicle[columnId];
        return (
          <div className="flex items-center space-x-1">
            <div className="w-3 h-3 rounded-full flex-shrink-0 border border-gray-300"
              style={{ backgroundColor: getColorCode(colorName) }}>
            </div>
            <span className="text-xs">{colorName || '-'}</span>
          </div>
        );

      case 'estatus':
        // Usamos getStatusColor y getStatusName directos de patiosData
        return (
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(vehicle.estatus)} whitespace-nowrap`}>
            {getStatusName(vehicle.estatus)}
          </span>
        );

      case 'detectado_rfid':
        return (
          <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-xs font-bold ${vehicle.detectado_rfid ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'
            }`}>
            {vehicle.detectado_rfid ? '✓' : '✗'}
          </span>
        );

      case 'last_read':
        return <span className="text-xs text-gray-500">{formatDateTime(vehicle.lectura_rfid?.last_read)}</span>;

      case 'FechaRecibo':
        return <span className="text-xs text-gray-500">{formatDate(vehicle.FechaRecibo)}</span>;

      case 'Catalogo':
        return <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-800">{vehicle.Catalogo || '-'}</span>;

      case 'VTE_DOCTO':
        const vteDocto = vehicle.VTE_DOCTO || vehicle.vte_docto;
        return <span className="text-xs whitespace-nowrap">{vteDocto || '-'}</span>;

      case 'vendedor':
        const vendedorVal = vehicle.vendedor || vehicle.Vendedor;
        return vendedorVal ? (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 whitespace-nowrap">
            {vendedorVal}
          </span>
        ) : (
          <span className="text-xs">-</span>
        );

      default:
        return <span className="text-sm text-gray-900 whitespace-nowrap">{vehicle[columnId] || '-'}</span>;
    }
  }, []);

  if (!currentItem) return <div>Cargando...</div>;

  return (
    <div className="w-full h-full flex flex-col">

      {/* HEADER STICKY */}
      <div className="sticky top-0 bg-white z-10 mb-3 sm:mb-4 pb-3 sm:pb-4 border-b">
        {/* Fila 1: back + título + badge */}
        <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
          <button onClick={onBackClick} className="p-2 hover:bg-gray-100 rounded-lg shrink-0"><ChevronLeft className="w-5 h-5" /></button>
          <displayInfo.icon className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 shrink-0" />
          <h2 className="text-base sm:text-xl font-bold truncate">{displayInfo.nombre}</h2>
          <span className={`text-xs sm:text-sm px-2 py-0.5 sm:py-1 rounded-full shrink-0 ${displayInfo.colorClass}`}>{displayInfo.tipo}</span>
        </div>

        {/* Fila 2: acciones + búsqueda */}
        <div className="flex items-center gap-2 2xl:ml-auto">
          <button onClick={handleExportExcel} className="flex items-center gap-2 px-2.5 sm:px-3 py-2 text-sm font-medium text-green-700 bg-green-50 hover:bg-green-100 rounded-lg transition-colors border border-green-200 shrink-0" title="Exportar a Excel"><FileDown className="w-4 h-4" /></button>
          <button onClick={handleExportPDF} className="flex items-center gap-2 px-2.5 sm:px-3 py-2 text-sm font-medium text-red-700 bg-red-50 hover:bg-red-100 rounded-lg transition-colors border border-red-200 shrink-0" title="Exportar a PDF"><FileText className="w-4 h-4" /></button>
          <button
            onClick={() => setIsColumnPopupOpen(true)}
            className="flex items-center gap-2 px-2.5 sm:px-3 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 rounded-lg transition-colors border border-gray-300 shrink-0"
            title="Configurar columnas"
          >
            <Settings className="w-4 h-4" />
          </button>
          <div className="relative flex-1 min-w-0">
            <input
              type="text"
              value={searchVIN}
              onChange={(e) => setSearchVIN(e.target.value)}
              className="border rounded p-2 w-full text-sm pl-8"
              placeholder="Buscar VIN o Inventario..."
            />
            <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          </div>
        </div>
      </div>

      {/* TABLA DINÁMICA */}
      <div className="flex-1 overflow-y-auto">
        {isLoadingConfig ? (
          <div className="text-center p-10">Cargando preferencias...</div>
        ) : vehiculosFiltrados.length > 0 ? (
          <div className="border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full table-auto">
                <thead className="bg-gray-50 border-b sticky top-0 z-10">
                  <tr>
                    <th className="px-3 py-3 text-left text-xs text-gray-600">#</th>
                    {visibleColumns.map((column) => (
                      <th
                        key={column.id}
                        className={`px-4 py-3 text-xs font-medium text-gray-700 uppercase tracking-wider ${column.width} ${shouldCenterColumn(column.id) ? 'text-center' : 'text-left'
                          } ${column.sortable ? 'cursor-pointer hover:bg-gray-100 select-none' : ''
                          }`}
                        onClick={column.sortable ? () => handleSort(column.id) : undefined}
                      >
                        <div className={`flex items-center space-x-1 ${shouldCenterColumn(column.id) ? 'justify-center' : ''
                          }`}>
                          <span className="whitespace-nowrap">{column.label}</span>
                          {column.sortable && getSortIcon(column.id)}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {getSortedVehicles(vehiculosFiltrados).map((vehicle, index) => (
                    <tr
                      key={vehicle.VIN || index}
                      className={`${vehicle.estatus === ESTATUS.ENTREGADO_STANDBY
                        ? 'bg-red-100 hover:bg-red-200'
                        : vehicle.estatus === ESTATUS.FACTURADO_NO_ENTREGADO
                          ? 'bg-yellow-100 hover:bg-yellow-200'
                          : vehicle.NoMay
                            ? 'bg-blue-100 hover:bg-blue-200'
                            : `hover:bg-gray-50 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-25'}`
                        } cursor-pointer transition-colors`}
                      onClick={() => setSelectedVehicle(vehicle)}
                    >
                      <td className="px-3 py-3 text-sm text-gray-500 font-mono">{index + 1}</td>
                      {visibleColumns.map((column) => (
                        <td
                          key={`${vehicle.VIN}-${column.id}`}
                          className={`px-4 py-3 text-sm text-gray-900 ${shouldCenterColumn(column.id) ? 'text-center' : ''
                            }`}
                        >
                          {renderCellContent(vehicle, column.id)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="p-12 text-center text-gray-500">No hay vehículos</div>
        )}
      </div>

      <ColumnReorderPopup
        isOpen={isColumnPopupOpen}
        onClose={() => setIsColumnPopupOpen(false)}
        allColumns={ALL_COLUMNS_CONFIG}
        currentColumns={visibleColumns}
        onSave={saveColumnOrder}
        defaultColumnsIds={DEFAULT_COLUMN_IDS}
      />

      <CarTraceabilityPopUp
        vehicle={selectedVehicle}
        isOpen={!!selectedVehicle}
        onClose={() => setSelectedVehicle(null)}
      />
    </div>
  );
};

export default PatioCard;
